<template>
  <section
    :id="module.id"
    class="job-data-section"
    :style="[jobDataSectionElStyles]"
  >
    <GlobalUIMedia
      v-if="module.config.general.showMedia"
      :media="module.config.general.media"
      :lazy="lazy"
    />
    <div class="job-data-section-wrapper" :style="modulePadding">
      <h2
        v-if="showHeader(module)"
        class="header"
        :style="headerElStyles"
        v-html="module.config.header.text"
      ></h2>
      <div
        class="content"
        :style="contentElStyles"
        v-html="jobDataContent"
      ></div>
    </div>
  </section>
</template>

<script>
import jobTextFieldModule from "./jobTextFieldDefaultModuleProp.js";
import exampleJobDataProp from "./exampleJobDataProp";

import { globalComputed, globalMethods } from "../GlobalUI/mixins/global.js";
import GlobalUIMedia from "../GlobalUI/GlobalUIMedia.vue";

export default {
  name: "JobTextField",
  components: { GlobalUIMedia },
  mixins: [globalComputed, globalMethods],
  props: {
    module: {
      type: Object,
      default: () => jobTextFieldModule,
    },
    lazy: {
      type: Boolean,
      default: false,
    },
    /**
     * Following values mapped to jobData properties:
     *
     * "introduction", => jobData.introduction
     * "description", => jobData.description
     * "about", => jobData.about
     * "expectation", => jobData.expectation
     * "offering", => jobData.offering
     * "contact_text", => jobData.contact_text
     * "additional", => jobData.additional
     * "language_skills", => jobData.language_skills[{title: "Deutsch", level: 3}]
     */
    jobData: {
      type: Object,
      default() {
        return exampleJobDataProp;
      },
    },
  },
  computed: {
    jobDataContent() {
      const field = this.module.config.general.text.offerField.value;

      if (field === "language_skills") {
        if (
          Array.isArray(this.jobData.language_skills) &&
          this.jobData.language_skills.length > 0
        ) {
          return this.jobData.language_skills
            .map((skill) => skill.title)
            .join(", ");
        }
        return "";
      }
      return this.jobData[field] || "";
    },
    jobDataSectionElStyles() {
      const { backgroundColor, template } = this.module.config.general;

      let alignItems = "center";
      let justifyContent = "center";
      let textAlign = "center";

      if (template.component === "JobTextFieldLeft") {
        alignItems = "flex-start";
        justifyContent = "flex-start";
        textAlign = "left";
      }

      return {
        backgroundColor: backgroundColor.value,
        textAlign,
        alignItems,
        justifyContent,
      };
    },
    contentElStyles() {
      const { textColor } = this.module.config.general.text;

      return {
        color: textColor.value,
      };
    },
    headerElStyles() {
      const { textColor, isTextInCaps } = this.module.config.header;

      return {
        color: textColor.value,
        textTransform: isTextInCaps ? "uppercase" : "initial",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../Gallery/galleryChildren";

@include reset;

.job-data-section {
  container-type: inline-size;
  contain: layout;
  display: flex;
  justify-content: center;

  &.-nested {
    isolation: isolate;
    overflow: hidden;
    min-height: 420px;
  }
}

.job-data-section-wrapper {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 25px;
  align-items: center;

  max-width: 1600px;
  margin: 0 auto;
  padding-inline: var(--cr-spacing-l);

  @include mobile {
    width: 100%;
    padding-inline: var(--cr-spacing-xs);
  }

  .-nested & {
    margin: 0;
    padding: var(--cr-spacing-xs);
  }

  .header {
    align-self: stretch;
    font-style: normal;
    hyphens: auto;

    @include font-family("heading");
    @include scale-fonts("heading-2");
    @include setup-richtext;

    .-nested & {
      @include scale-fonts("heading-1");
    }
  }

  .content {
    align-self: stretch;
    font-style: normal;

    @include font-family("body");
    @include scale-fonts("body");
    @include setup-richtext;
  }
}
</style>
